import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";
import { allArticles } from "@/mocks/articles";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [guideDropdownOpen, setGuideDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const closeMobile = () => setMobileOpen(false);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 font-heading text-lg md:text-xl font-bold text-foreground-950 tracking-tight"
          >
            <span className="text-primary-600">OOPBUY</span>
            <span className="text-foreground-700">Spreadsheet</span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-6">
            <Link
              to="/"
              className="flex items-center gap-1.5 text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-home-line"></i>
              </span>
              Home
            </Link>

            {/* Categories Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setDropdownOpen(true)}
              onMouseLeave={() => setDropdownOpen(false)}
            >
              <button className="flex items-center gap-1 text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                Kategorien
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={`ri-arrow-down-s-line transition-transform ${dropdownOpen ? "rotate-180" : ""}`}></i>
                </span>
              </button>
              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-background-50 rounded-xl shadow-lg border border-background-200/70 py-2 overflow-hidden max-h-[70vh] overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors"
                      onClick={closeMobile}
                    >
                      <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                        <i className={cat.icon}></i>
                      </span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Shopping Guide Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setGuideDropdownOpen(true)}
              onMouseLeave={() => setGuideDropdownOpen(false)}
            >
              <button className="flex items-center gap-1 text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-book-open-line"></i>
                </span>
                Einkaufsführer
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={`ri-arrow-down-s-line transition-transform ${guideDropdownOpen ? "rotate-180" : ""}`}></i>
                </span>
              </button>
              {guideDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-72 bg-background-50 rounded-xl shadow-lg border border-background-200/70 py-2 overflow-hidden max-h-[70vh] overflow-y-auto">
                  {allArticles.map((article) => (
                    <Link
                      key={article.slug}
                      to={`/guide/${article.slug}`}
                      className="flex items-start gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors"
                      onClick={closeMobile}
                    >
                      <span className="w-5 h-5 flex items-center justify-center text-primary-500 mt-0.5 shrink-0">
                        <i className="ri-article-line"></i>
                      </span>
                      <div className="min-w-0">
                        <span className="block text-sm leading-tight truncate">{article.title}</span>
                        <span className="text-xs text-foreground-400">{article.readTime}</span>
                      </div>
                    </Link>
                  ))}
                  <div className="border-t border-background-200/70 mt-1 pt-1">
                    <Link
                      to="/sitemap"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-primary-600 hover:bg-primary-50 transition-colors"
                      onClick={closeMobile}
                    >
                      <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                        <i className="ri-map-pin-line"></i>
                      </span>
                      Alle Guides — Sitemap
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 text-sm font-medium text-primary-700 bg-primary-50 rounded-md hover:bg-primary-100 transition-colors whitespace-nowrap"
            >
              oopbuy spreadsheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 text-sm font-medium text-background-50 bg-primary-600 rounded-md hover:bg-primary-700 transition-colors whitespace-nowrap"
            >
              buy it now
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden w-8 h-8 flex items-center justify-center text-foreground-700 cursor-pointer"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Menu"
          >
            <i className={mobileOpen ? "ri-close-line" : "ri-menu-line"}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-background-50 border-t border-background-200/70 max-h-[80vh] overflow-y-auto">
          {/* Home & Sitemap */}
          <div className="px-4 py-3 space-y-1">
            <Link
              to="/"
              className="flex items-center gap-3 py-2.5 text-sm font-medium text-foreground-700"
              onClick={closeMobile}
            >
              <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                <i className="ri-home-line"></i>
              </span>
              Home
            </Link>
            <Link
              to="/sitemap"
              className="flex items-center gap-3 py-2.5 text-sm font-medium text-foreground-700"
              onClick={closeMobile}
            >
              <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                <i className="ri-map-pin-line"></i>
              </span>
              Sitemap
            </Link>
          </div>

          {/* Categories */}
          <div className="border-t border-background-200/70 px-4 py-3">
            <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">
              Kategorien
            </p>
            <div className="grid grid-cols-2 gap-2">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 p-2 bg-background-100 rounded-lg border border-background-200/70 hover:border-primary-300 transition-all"
                  onClick={closeMobile}
                >
                  <span className="w-6 h-6 flex items-center justify-center rounded-full bg-background-50 text-primary-500 shrink-0">
                    <i className={`${cat.icon} text-xs`}></i>
                  </span>
                  <span className="text-xs font-medium text-foreground-700 leading-tight">{cat.name}</span>
                </a>
              ))}
            </div>
          </div>

          {/* Shopping Guide */}
          <div className="border-t border-background-200/70 px-4 py-3">
            <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">
              Einkaufsführer
            </p>
            <div className="space-y-1">
              {allArticles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/guide/${article.slug}`}
                  className="flex items-center gap-3 py-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors"
                  onClick={closeMobile}
                >
                  <span className="w-4 h-4 flex items-center justify-center text-primary-400 shrink-0">
                    <i className="ri-article-line"></i>
                  </span>
                  <span className="truncate text-xs">{article.title}</span>
                </Link>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="border-t border-background-200/70 px-4 py-4 flex flex-col gap-2">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2.5 text-sm font-medium text-center text-primary-700 bg-primary-50 rounded-md"
              onClick={closeMobile}
            >
              oopbuy spreadsheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2.5 text-sm font-medium text-center text-background-50 bg-primary-600 rounded-md"
              onClick={closeMobile}
            >
              buy it now
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}